/**
 * MapProvider abstraction for the Admin Live Tracking Map (ODE-P07-T02,
 * REQ-084 / OD-009).
 *
 * OD-009 approves Google Maps (Maps JavaScript API for Admin Web) while
 * requiring an abstraction layer so the vendor can be swapped. This module
 * is that abstraction: a small `MapProvider` port plus a runtime-gated
 * registry that resolves the concrete adapter from configuration:
 *
 *   - `VITE_MAP_PROVIDER=fake`    → the hermetic FakeMapAdapter (dev/demo)
 *   - key present (env flag)      → GoogleMapsJsMapAdapter, runtime-loaded
 *   - no key / unknown provider   → explicit `misconfigured` resolution
 *                                    (rendered as a REQ-090 state, never a crash)
 *
 * No Google Maps API key is ever committed: the key only travels through
 * `VITE_GOOGLE_MAPS_API_KEY` at runtime/build time, and the Google adapter
 * is loaded ONLY when that key (or an explicit provider flag) is present.
 * All tests use the fake adapter — no external map network in CI.
 *
 * Traces to: ODE-PRD-REQ-084, ODE-PRD-REQ-090, ODE-OD-009.
 */

/**
 * One marker to plot — a server-derived live position (REQ-043). `latitude`
 * / `longitude` are the LAST KNOWN FIX from the overview snapshot; they are
 * never extrapolated, interpolated, or fabricated by any adapter.
 */
export interface MapMarkerView {
  /** Tracking session id (stable marker identity). */
  readonly id: string;
  readonly latitude: number;
  readonly longitude: number;
  /** Short human label (driver · delivery order). */
  readonly label: string;
  /** Freshness vs the server-published staleAfterMs bound (REQ-090). */
  readonly stale: boolean;
}

/**
 * The MapProvider port (REQ-084): mount into a container, render the given
 * markers, and release all resources on destroy. Implementations must be
 * fail-safe: they never invent positions and never throw for missing
 * configuration (the registry gates that before construction).
 */
export interface MapProvider {
  /** Adapter identity for diagnostics (`google` | `fake`). */
  readonly name: string;
  mount(container: HTMLElement): void;
  setMarkers(markers: readonly MapMarkerView[]): void;
  destroy(): void;
}

/** Result of resolving the map provider configuration (fail-closed). */
export type MapProviderResolution =
  | { readonly kind: 'google'; readonly apiKey: string }
  | { readonly kind: 'fake' }
  | { readonly kind: 'misconfigured'; readonly reason: string };

/** Environment inputs for the registry (explicit, injected — never global). */
export interface MapProviderEnv {
  /** `VITE_MAP_PROVIDER` — `google` (default) or `fake`. */
  readonly provider?: string | null;
  /** `VITE_GOOGLE_MAPS_API_KEY` — runtime key; never committed. */
  readonly apiKey?: string | null;
}

/** The exact environment variable names the registry reads. */
export const MAP_PROVIDER_ENV_KEYS = {
  provider: 'VITE_MAP_PROVIDER',
  apiKey: 'VITE_GOOGLE_MAPS_API_KEY',
} as const;

/**
 * Pure fail-closed resolution (REQ-084 / OD-009 gating):
 *
 *   provider=fake                         → fake (regardless of key)
 *   provider empty/google + key present   → google (runtime-loaded)
 *   provider empty/google + key absent    → misconfigured (explicit)
 *   any other provider value              → misconfigured (explicit)
 */
export function resolveMapProviderConfig(env: MapProviderEnv): MapProviderResolution {
  const provider = (env.provider ?? '').trim().toLowerCase();
  const apiKey = (env.apiKey ?? '').trim();

  if (provider === 'fake') {
    return { kind: 'fake' };
  }
  if (provider === '' || provider === 'google') {
    if (apiKey === '') {
      return {
        kind: 'misconfigured',
        reason: `Peta belum dikonfigurasi: kunci Google Maps (${MAP_PROVIDER_ENV_KEYS.apiKey}) tidak ditemukan.`,
      };
    }
    return { kind: 'google', apiKey };
  }
  return {
    kind: 'misconfigured',
    reason: `Konfigurasi peta tidak dikenal: ${MAP_PROVIDER_ENV_KEYS.provider}="${provider}" (didukung: google, fake).`,
  };
}

/**
 * Reads the map provider environment from `import.meta.env` (Vite). Kept
 * small and defensive so non-browser contexts (tests, node) yield the
 * explicit misconfigured path instead of a crash.
 */
export function mapEnvFromImportMeta(): MapProviderEnv {
  const meta = import.meta as unknown as { env?: Record<string, unknown> } | undefined;
  const env = meta?.env ?? {};
  const read = (key: string): string | null =>
    typeof env[key] === 'string' ? (env[key] as string) : null;
  return {
    provider: read(MAP_PROVIDER_ENV_KEYS.provider),
    apiKey: read(MAP_PROVIDER_ENV_KEYS.apiKey),
  };
}
